import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArcReactor, HudPanel } from '../components/hud';

/**
 * Sign-in. Under the Stark theme it reads as a boot screen — the reactor
 * spins up once and a single scan passes over the panel — but underneath it
 * is a plain, labelled form that works the same in every theme.
 */
export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [today] = useState(() => new Date());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = today
    .toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    .toUpperCase();

  return (
    <main className="boot">
      <div className="boot-inner">
        <div className="boot-brand">
          <ArcReactor size={88} booting />
          <h1 className="boot-title">
            BONDHQ<span className="boot-title-sep" aria-hidden="true">//</span>
            <span className="sr-only"> — </span>
            <span className="boot-title-sub">SURETY INTELLIGENCE</span>
          </h1>
        </div>

        <HudPanel title="Sign in" meta="Operator access" className="boot-panel" headingLevel={2}>
          <form className="boot-form" onSubmit={handleSubmit}>
            <div>
              <label className="hud-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="form-control"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div>
              <label className="hud-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="form-control"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            {error && <div id="login-error" className="boot-error" role="alert">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary boot-submit"
              disabled={submitting || !email || !password}
              aria-busy={submitting || undefined}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </HudPanel>

        <div className="boot-foot">
          <span className="hud-label">{dateLabel}</span>
          <span className="hud-label">Bond agent console</span>
        </div>
        <p className="boot-help">Locked out? Ask your BondHQ administrator to reset your access.</p>
      </div>
    </main>
  );
}
